import { initCandidateTags } from "./candidate_tags";
import { initScheduleDate } from "./schedule_date";
import { initSummernote } from "./summernote";
import { mountLogos } from "./utils/bots_prospects/mount_logo";
import { innerPopupOpen, syncPageScrollLock } from "./qlfr_modal";

type DrawerSection = "notes" | "followup" | "personalize" | null;

const SECTION_TRIGGERS: ReadonlyArray<[string, Exclude<DrawerSection, null>]> = [
  [".notes-button", "notes"],
  [".schedule-button", "followup"],
  [".customize-message-button", "personalize"],
];

let listenersBound = false;

// gap between the Reject button and its menu, and the menu and the screen edge
const REJECT_MENU_GAP = 8;
const REJECT_MENU_EDGE = 12;

function fitRejectMenu(details: HTMLDetailsElement): void {
  const trigger = details.querySelector<HTMLElement>("summary");
  const menu = details.querySelector<HTMLElement>(".cd-reject-menu");
  if (!trigger || !menu) return;

  details.classList.remove("cd-reject-down", "cd-reject-end");
  menu.style.maxHeight = "";

  const anchor = trigger.getBoundingClientRect();
  const above = anchor.top - REJECT_MENU_GAP - REJECT_MENU_EDGE;
  const below = window.innerHeight - anchor.bottom - REJECT_MENU_GAP - REJECT_MENU_EDGE;
  const height = menu.scrollHeight;

  const down = height > above && below > above;
  details.classList.toggle("cd-reject-down", down);
  menu.style.maxHeight = `${Math.max(0, Math.floor(down ? below : above))}px`;

  if (menu.getBoundingClientRect().right > window.innerWidth - REJECT_MENU_EDGE) {
    details.classList.add("cd-reject-end");
  }
}

const drawerEl = () => document.getElementById("candidate-drawer");
const backdropEl = () => document.getElementById("candidate-drawer-backdrop");

export function initCandidateDrawer(): void {
  if (listenersBound) return;
  if (!drawerEl() || !backdropEl()) return;
  listenersBound = true;

  const spinner =
    '<div class="drawer-body" style="padding:48px 22px;text-align:center"><div class="spinner-border text-primary"></div></div>';
  const phoneIcon =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M5 4h4l2 5-3 2a12 12 0 005 5l2-3 5 2v4a2 2 0 01-2 2A16 16 0 013 6a2 2 0 012-2z"/></svg>';
  const escape = (value: string) => {
    const element = document.createElement("div");
    element.textContent = value;
    return element.innerHTML;
  };
  const renderPhoneRetry = (row: HTMLElement, url: string, prospectId: string | undefined, message: string) => {
    const prospectIdAttribute = prospectId ? ` data-prospect-id="${escape(prospectId)}"` : "";
    row.innerHTML = `<span class="cbtn">${phoneIcon}</span><div style="flex:1;min-width:0"><div class="cr-title">${message}</div></div><button type="button" class="btn btn-sm" data-phone-fetch${prospectIdAttribute} data-url="${escape(url)}">Retry phone lookup</button>`;
  };
  const updateExternalPhoneButtons = (prospectId: string, phoneNumber: string) => {
    document
      .querySelectorAll<HTMLButtonElement>(`.phone-button[data-prospect-id="${prospectId}"]`)
      .forEach((button) => {
        button.dataset.phoneNumber = phoneNumber;
        button.classList.remove("not-found");
        button.classList.add("found");
        ["data-toggle", "data-placement", "data-content", "data-html", "trigger"].forEach((attribute) =>
          button.removeAttribute(attribute),
        );
        $(button).popover("dispose");
      });
  };

  const updateExternalEmailButtons = (botsProspectId: string, emails: unknown[]) => {
    document.querySelectorAll<HTMLElement>(`.email-button-${botsProspectId}`).forEach((button) => {
      button.dataset.emails = JSON.stringify(emails);
      button.dataset.emailFetched = "true";
      button.classList.toggle("found", emails.length > 0);
      button.classList.toggle("not-found", emails.length === 0);
    });
  };

  // The page behind the card must not scroll. A MutationObserver in qlfr_modal
  // would catch the class change anyway, but only on the next frame — calling it
  // here locks on the same tick the card appears.
  const open = () => {
    drawerEl()?.classList.add("open");
    backdropEl()?.classList.add("open");
    drawerEl()?.setAttribute("aria-hidden", "false");
    syncPageScrollLock();
  };
  const close = () => {
    drawerEl()?.classList.remove("open");
    backdropEl()?.classList.remove("open");
    drawerEl()?.setAttribute("aria-hidden", "true");
    syncPageScrollLock();
  };

  const revealSection = (section: DrawerSection) => {
    if (!section) return;
    window.requestAnimationFrame(() => {
      const drawer = drawerEl();
      if (!drawer) return;

      drawer
        .querySelector<HTMLElement>(`[data-drawer-section="${section}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
      if (section === "notes") {
        const noteInput = drawer.querySelector<HTMLTextAreaElement>("[data-note-input]");
        if (noteInput) $(noteInput).summernote("focus");
      } else if (section === "followup") {
        // open the inline follow-up editor so a draft can be written straight away
        drawer.querySelector<HTMLElement>("[data-fu-open]")?.click();
      } else if (section === "personalize") {
        // same for the personalize editor — the trigger may be in the drawer footer
        document.querySelector<HTMLElement>("[data-pm-open]")?.click();
      }
    });
  };

  const destroyEditors = () => {
    const drawer = drawerEl();
    if (!drawer) return;
    drawer
      .querySelectorAll<HTMLElement>("[data-note-input], .summernote, .pm-body")
      .forEach((el) => $(el).summernote("destroy"));
  };

  const loadDrawer = (url: string, section: DrawerSection = null) => {
    const drawer = drawerEl();
    if (!drawer) return;

    destroyEditors();
    drawer.innerHTML = spinner;
    open();
    fetch(url, { headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" } })
      .then((r) => r.text())
      .then((html) => {
        // re-resolve: a Turbo visit may have swapped the drawer mid-flight
        const target = drawerEl();
        if (!target) return;

        target.innerHTML = html;
        initCandidateTags(target);
        initScheduleDate(); // re-init the follow-up scheduler for the injected form
        initSummernote(); // wire the "Add a note" summernote editor in the injected body
        mountLogos();
        revealSection(section);
      })
      .catch(() => {
        const target = drawerEl();
        if (target) {
          target.innerHTML = '<div class="drawer-body" style="padding:40px 22px">Failed to load candidate.</div>';
        }
      });
  };

  document.addEventListener(
    "click",
    (e) => {
      const target = e.target as HTMLElement;

      let actionBtn: HTMLElement | null = null;
      let section: DrawerSection = null;
      for (const [selector, name] of SECTION_TRIGGERS) {
        const btn = target.closest<HTMLElement>(selector);
        if (btn) {
          actionBtn = btn;
          section = name;
          break;
        }
      }
      if (!actionBtn) return;

      const opener =
        actionBtn.closest<HTMLElement>("[data-candidate-open]") ||
        actionBtn.closest("tr")?.querySelector<HTMLElement>("[data-candidate-open]");
      const url = opener?.dataset.url;
      if (!url) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      loadDrawer(url, section);
    },
    true, // capture phase
  );

  document.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;

    const phoneButton = target.closest<HTMLButtonElement>("[data-phone-fetch]");
    if (phoneButton) {
      const url = phoneButton.dataset.url;
      const prospectId = phoneButton.dataset.prospectId;
      const row = phoneButton.closest<HTMLElement>(".contact-row");
      if (!url || !row || phoneButton.disabled) return;

      phoneButton.disabled = true;
      phoneButton.textContent = "Fetching phone number...";

      fetch(url, {
        method: "POST",
        headers: {
          "X-CSRF-Token": (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content || "",
          Accept: "application/json",
        },
      })
        .then((response) => {
          if (!response.ok) throw new Error(String(response.status));
          return response.json();
        })
        .then((data) => {
          if (data.status === "found" && data.phone_number) {
            const phoneNumber = String(data.phone_number);
            row.innerHTML = `<span class="cbtn found">${phoneIcon}</span><div style="flex:1;min-width:0"><div class="cr-title">${escape(phoneNumber)}</div></div>`;
            if (prospectId) updateExternalPhoneButtons(prospectId, phoneNumber);
          } else {
            renderPhoneRetry(row, url, prospectId, "No phone number found");
          }
        })
        .catch(() => renderPhoneRetry(row, url, prospectId, "Could not fetch phone number"));
      return;
    }

    const emailButton = target.closest<HTMLButtonElement>("[data-email-fetch]");
    if (emailButton) {
      const url = emailButton.dataset.url;
      const section = emailButton.closest<HTMLElement>("[data-drawer-email-contact]");
      if (!url || !section || emailButton.disabled) return;

      const label = emailButton.innerHTML;
      emailButton.disabled = true;
      emailButton.innerHTML = '<i class="fas fa-spinner fa-spin"></i>';

      fetch(url, {
        method: "POST",
        headers: {
          "X-CSRF-Token": (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content || "",
          Accept: "application/json",
        },
      })
        .then((response) => {
          if (!response.ok) throw new Error(String(response.status));
          return response.json();
        })
        .then((data) => {
          section.outerHTML = data.html;
          const botsProspectId = emailButton.dataset.botsProspectId;
          if (botsProspectId) updateExternalEmailButtons(botsProspectId, data.email_addresses || []);
        })
        .catch(() => {
          emailButton.disabled = false;
          emailButton.innerHTML = label;
          window.alert("Failed to fetch email address");
        });
      return;
    }

    const trigger = target.closest<HTMLElement>("[data-candidate-open]");
    if (trigger) {
      // .view-full-data opens the shared full-information modal, so a click on it
      // is not a request for the drawer. contacted.ts guards its row click the same way.
      if (target.closest("a, button, input, label, select, textarea, .view-full-data")) return;
      e.preventDefault();
      const url = trigger.dataset.url;
      if (!url) return;
      loadDrawer(url);
      return;
    }

    if (target.closest("[data-drawer-close]")) close();
  });

  document.addEventListener(
    "toggle",
    (e) => {
      const menu = e.target as HTMLElement;
      if (menu instanceof HTMLDetailsElement && menu.matches("[data-cd-reject]") && menu.open) fitRejectMenu(menu);
    },
    true
  );
  window.addEventListener("resize", () => {
    document.querySelectorAll<HTMLDetailsElement>("[data-cd-reject][open]").forEach(fitRejectMenu);
  });

  document.addEventListener("click", (e) => {
    const inside = (e.target as HTMLElement).closest("[data-cd-reject]");
    document.querySelectorAll<HTMLDetailsElement>("[data-cd-reject][open]").forEach((menu) => {
      if (menu !== inside) menu.open = false;
    });
  });

  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    // an open Reject menu takes Escape before the card does
    const rejectMenu = document.querySelector<HTMLDetailsElement>("[data-cd-reject][open]");
    if (rejectMenu) {
      rejectMenu.open = false;
      return;
    }

    if (!innerPopupOpen()) close();
  });
}
